<script lang="ts">
import { onMount } from 'svelte';
import CardView from '$components/Card/CardView.svelte';
import FilterBar from '$components/Filter/FilterBar.svelte';
import RatingBar from '$components/Layout/RatingBar.svelte';
import Topbar from '$components/Layout/Topbar.svelte';
import ExamView from '$components/Modes/ExamView.svelte';
import FachgespraechView from '$components/Modes/FachgespraechView.svelte';
import SprintView from '$components/Modes/SprintView.svelte';
import StatsView from '$components/Modes/StatsView.svelte';
import SelfExplainPanel from '$components/SelfExplain/SelfExplainPanel.svelte';
import { loadCards } from '$lib/cards/loader';
import { previewIntervals } from '$lib/fsrs';
import { keyAction } from '$lib/keyboard';
import { nextCursor } from '$lib/queue';
import { clozeAnswerText, clozePromptText } from '$lib/render/cloze';
import { richTextToPlain } from '$lib/render/richtext';
import { dueHint, userStats } from '$lib/stats';
import { type PersistedFilterState, WebtrainerStore } from '$lib/store';
import { Tts } from '$lib/tts';
import type { Card, Rating } from '$types/card';

const store = new WebtrainerStore(globalThis.localStorage);
const tts = new Tts();

let allCards = $state<Card[]>([]);
let dueIds = $state<string[]>([]);
let cursor = $state(0);
let revealed = $state(false);
let occlusionIndex = $state(0);
let fadeLevel = $state(0);
let loadingError = $state<string | null>(null);
let filterState = $state<PersistedFilterState>({ ...store.filter });
let logVersion = $state(0); // re-trigger derived stats on review
let showShortcuts = $state(true);
let backupStatus = $state<string | null>(null);
let fileInput = $state<HTMLInputElement | null>(null);

type View = 'lernen' | 'pruefung' | 'sprint' | 'fachgespraech' | 'statistik';
let view = $state<View>('lernen');
const MODES: { id: View; label: string }[] = [
  { id: 'lernen', label: 'lernen' },
  { id: 'pruefung', label: 'prüfung' },
  { id: 'sprint', label: 'tabellenbuch' },
  { id: 'fachgespraech', label: 'fachgespräch' },
  { id: 'statistik', label: 'statistik' },
];

const currentCard = $derived(allCards.find((c) => c.id === dueIds[cursor]) ?? null);
const canUndo = $derived(((_v: number) => store.canUndo)(logVersion));
const stats = $derived(((_v: number) => userStats(store.exportReviewLog()))(logVersion));
const ratingHints = $derived.by<Partial<Record<Rating, string>>>(() => {
  if (!currentCard) return {};
  const now = new Date();
  const due = previewIntervals(store.getCardState(currentCard.id, now), now);
  const nowMs = now.getTime();
  return {
    1: dueHint(due[1], nowMs),
    2: dueHint(due[2], nowMs),
    3: dueHint(due[3], nowMs),
    4: dueHint(due[4], nowMs),
  };
});

function refreshDue(): void {
  dueIds = store.filteredDueIds(allCards);
  cursor = Math.min(cursor, Math.max(0, dueIds.length - 1));
  revealed = false;
}

function applyFilterChange(partial: Partial<PersistedFilterState>): void {
  filterState = { ...filterState, ...partial };
  store.setFilter(partial);
  refreshDue();
}

function rate(r: Rating): void {
  if (!currentCard) return;
  const reviewedId = currentCard.id;
  store.applyReview(reviewedId, r);
  logVersion++;
  refreshDue(); // erst dueIds aktualisieren (bewertete Karte fällt raus) …
  cursor = nextCursor(cursor, dueIds, reviewedId); // … dann Cursor setzen (kein Skip)
  revealed = false;
  occlusionIndex = 0;
}

function undo(): void {
  const id = store.undoLastReview();
  if (!id) return;
  logVersion++;
  refreshDue();
  const idx = dueIds.indexOf(id);
  if (idx >= 0) cursor = idx;
  revealed = false;
}

function downloadBackup(): void {
  const blob = new Blob([store.exportBackup()], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `shk-backup-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
  backupStatus = '✓ backup heruntergeladen';
}

async function onImportFile(e: Event): Promise<void> {
  const input = e.currentTarget as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  try {
    const info = store.importBackup(await file.text());
    logVersion++;
    refreshDue();
    backupStatus = `✓ import: ${info.cards} karten-states, ${info.reviews} reviews`;
  } catch (err) {
    backupStatus = `✗ import fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`;
  } finally {
    input.value = '';
  }
}

function speak(side: 'front' | 'back'): void {
  if (!currentCard) return;
  let text = '';
  if (currentCard.type === 'cloze')
    text = side === 'front' ? clozePromptText(currentCard) : clozeAnswerText(currentCard);
  else if ('front' in currentCard)
    text = side === 'front' ? (currentCard.front ?? '') : (currentCard.back ?? '');
  if (text) tts.speak(richTextToPlain(text));
}

function onKey(e: KeyboardEvent): void {
  if (view !== 'lernen') return; // Shortcuts gehören dem Study-Loop
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;
  const action = keyAction(e.key, { revealed, hasCard: !!currentCard, shiftKey: e.shiftKey });
  switch (action.kind) {
    case 'reveal':
      e.preventDefault();
      revealed = !revealed;
      break;
    case 'rate':
      rate(action.rating);
      break;
    case 'speak':
      speak(action.side);
      break;
    case 'fade':
      fadeLevel = (fadeLevel + 1) % 4;
      break;
    case 'undo':
      undo();
      break;
  }
}

onMount(() => {
  // Synchroner onMount → Svelte ehrt die Cleanup-Rückgabe (bei async wird sie
  // ignoriert ⇒ keydown-Listener-Leak). Karten-Load läuft fire-and-forget.
  window.addEventListener('keydown', onKey);
  void (async () => {
    try {
      allCards = await loadCards();
      refreshDue();
    } catch (e) {
      loadingError = e instanceof Error ? e.message : String(e);
    }
  })();
  return () => window.removeEventListener('keydown', onKey);
});
</script>

<div class="grain" aria-hidden="true"></div>

<div class="app">
  <Topbar {stats} />

  <main class="page">
    <nav class="mode-nav" aria-label="Modus wählen">
      {#each MODES as m}
        <button
          type="button"
          class="mode-tab"
          class:active={view === m.id}
          onclick={() => (view = m.id)}>
          {m.label}
        </button>
      {/each}
    </nav>

    {#if view === 'pruefung'}
      <ExamView cards={store.applyFilter(allCards)} />
    {:else if view === 'sprint'}
      <SprintView cards={allCards} />
    {:else if view === 'fachgespraech'}
      <FachgespraechView cards={allCards} />
    {:else if view === 'statistik'}
      <StatsView cards={allCards} log={store.exportReviewLog()} />
    {:else}
    <FilterBar cards={allCards} filter={filterState} onChange={applyFilterChange} />

    <section class="study">
      {#if loadingError}
        <p class="error">[ FEHLER ] {loadingError}</p>
      {:else if !allCards.length}
        <div class="rule"><span class="rule-label">[ LADE KARTEN ]</span><span class="rule-line"></span></div>
        <p class="muted-line">› cards.json wird geladen …</p>
      {:else if !currentCard}
        <div class="rule"><span class="rule-label">[ KEINE FAELLIG ]</span><span class="rule-line"></span></div>
        <p class="muted-line">
          🎉 alle gefilterten karten sind gelernt. modus auf <b>studium</b> oder <b>alles</b> umstellen, oder filter zurücksetzen.
        </p>
      {:else}
        <CardView card={currentCard} {revealed} {occlusionIndex} {fadeLevel} position={cursor + 1} total={dueIds.length} />

        <div class="cv-actions">
          <button type="button" class="btn btn-primary" onclick={() => (revealed = !revealed)}>
            {revealed ? 'nochmal verstecken' : 'antwort zeigen'}
            {#if showShortcuts}<span class="kbd">SPACE</span>{/if}
          </button>
          <button type="button" class="btn btn-ghost" onclick={() => speak(revealed ? 'back' : 'front')}>
            🔊 vorlesen{#if showShortcuts}<span class="kbd">S</span>{/if}
          </button>
          {#if currentCard.type === 'calc' || currentCard.type === 'faded'}
            <button type="button" class="btn btn-ghost" onclick={() => (fadeLevel = (fadeLevel + 1) % 4)}>
              fade {fadeLevel}{#if showShortcuts}<span class="kbd">F</span>{/if}
            </button>
          {/if}
        </div>

        <RatingBar onRate={rate} disabled={!revealed} {showShortcuts} hints={ratingHints} />

        <SelfExplainPanel card={currentCard} />
      {/if}

      {#if canUndo}
        <div class="undo-row">
          <button type="button" class="undo-btn" onclick={undo}>
            ↶ letzte bewertung zurück{#if showShortcuts}<span class="kbd">U</span>{/if}
          </button>
        </div>
      {/if}
    </section>
    {/if}

    <div class="data-row">
      <button type="button" class="mini-btn" onclick={downloadBackup}>⬇ backup</button>
      <button type="button" class="mini-btn" onclick={() => fileInput?.click()}>⬆ import</button>
      <input
        type="file"
        accept="application/json,.json"
        bind:this={fileInput}
        onchange={onImportFile}
        hidden />
      {#if backupStatus}<span class="data-status">{backupStatus}</span>{/if}
    </div>

    <footer class="footer-meta">
      <span>► shk·werk v2</span>
      <span>│</span>
      <span>{allCards.length} karten</span>
      <span>│</span>
      <span>{stats.totalReviews} reviews gesamt</span>
      <span>│</span>
      <span>{Math.round(stats.accuracy * 100)}% trefferquote</span>
      <span>│</span>
      <span>fsrs-6 · ts-fsrs · svelte 5</span>
    </footer>
  </main>
</div>

<style>
  .app { position: relative; z-index: 2; }
  .page { max-width: 880px; margin: 0 auto; padding: 8px var(--pad) 80px; }

  .mode-nav {
    display: flex;
    gap: 0;
    margin-top: 10px;
    border: 1px solid var(--rule);
    background: var(--paper);
    overflow-x: auto;
  }
  .mode-tab {
    flex: 1;
    background: transparent;
    border: none;
    border-right: 1px solid var(--rule);
    padding: 8px 10px;
    font-family: var(--font-mono);
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--ink-2);
    cursor: pointer;
    white-space: nowrap;
  }
  .mode-tab:last-child { border-right: none; }
  .mode-tab:hover { background: var(--paper-2); }
  .mode-tab.active { background: var(--ink); color: var(--paper); }
  .study { margin-top: 4px; }
  .error { color: var(--r-again); font-weight: 700; }
  .muted-line { color: var(--ink-2); font-size: 13px; }

  .cv-actions {
    display: flex;
    gap: 0;
    border: 2px solid var(--ink);
    border-top: 0;
    background: var(--paper);
    margin-top: 0;
    box-shadow: 6px 6px 0 var(--ink-3);
  }
  .btn {
    flex: 1;
    border: none;
    background: var(--paper);
    padding: 14px 10px;
    font-family: var(--font-mono);
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--ink);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border-right: 1px solid var(--rule);
    min-height: 48px;
    cursor: pointer;
  }
  .btn:last-child { border-right: none; }
  .btn-primary {
    background: var(--ink);
    color: var(--paper);
    flex: 2;
  }
  .btn-primary:hover { background: var(--accent); }
  .btn-ghost:hover { background: var(--paper-2); }
  .btn .kbd { margin-left: 6px; }

  .undo-row { display: flex; justify-content: flex-end; margin-top: 8px; }
  .undo-btn {
    background: transparent;
    border: 1px dashed var(--rule);
    color: var(--ink-2);
    padding: 4px 10px;
    font-family: var(--font-mono);
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    cursor: pointer;
  }
  .undo-btn:hover { border-color: var(--accent); color: var(--accent-2); }

  .data-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 28px;
    padding-top: 10px;
    border-top: 1px dashed var(--rule);
  }
  .mini-btn {
    background: transparent;
    border: 1px solid var(--rule);
    color: var(--ink-2);
    padding: 4px 10px;
    font-family: var(--font-mono);
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    cursor: pointer;
  }
  .mini-btn:hover { background: var(--paper-2); }
  .data-status { font-size: 10px; color: var(--muted); letter-spacing: 0.04em; }

  .footer-meta {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    font-size: 10px;
    color: var(--muted);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 24px 0;
    margin-top: 24px;
    border-top: 1px dashed var(--rule);
  }

  .rule {
    display: flex; align-items: center; gap: 10px;
    margin: 16px 0 8px;
    font-size: 11px; letter-spacing: 0.08em;
    color: var(--ink-3); text-transform: uppercase;
  }
  .rule-label { white-space: nowrap; font-weight: 600; }
  .rule-line { flex: 1; height: 0; border-top: 1px dashed var(--rule); opacity: 0.5; }
</style>
